import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { ArrowLeft, LockKey, SpinnerGap } from '../components/ui/icons'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { WorkspaceExperience } from '../components/WorkspaceExperience'
import { publicPreviewById } from '../data/previews'
import { api } from '../lib/api'
import { useWorkspaceSession } from '../lib/workspace-session'
import type { PublicWorkspace } from '../types'
import { NotFoundPage } from './NotFoundPage'

type Gate = 'loading' | 'email' | 'code' | 'open' | 'missing'

export function CustomerWorkspacePage() {
  const { publicId = '' } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const entryState = location.state as { email?: string; codeRequested?: boolean } | null
  const [gate, setGate] = useState<Gate>('loading')
  const [workspace, setWorkspace] = useState<PublicWorkspace | null>(null)
  const [email, setEmail] = useState(entryState?.email || '')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const previewMode = import.meta.env.DEV && Boolean(publicPreviewById(publicId))
  const recordWorkspaceSession = useWorkspaceSession(publicId, gate === 'open' && Boolean(workspace) && !previewMode)
  // Each order form event is reported once per form per page load; the Worker also limits alerts to one a day.
  const reportedOrderFormActivity = useRef(new Set<string>())
  const reportOrderFormActivity = useCallback((formId: string, type: 'viewed' | 'signing_started') => {
    const key = `${formId}:${type}`
    if (reportedOrderFormActivity.current.has(key)) return
    reportedOrderFormActivity.current.add(key)
    void api.reportOrderFormActivity(publicId, formId, type).catch(() => undefined)
  }, [publicId])

  const load = useCallback(async () => {
    const preview = import.meta.env.DEV ? publicPreviewById(publicId) : null
    if (preview) {
      setWorkspace(preview)
      setEmail(preview.viewerEmail)
      setGate('open')
      return
    }
    try {
      const data = await api.loadWorkspace(publicId)
      setWorkspace(data)
      setGate('open')
      if (data.publicId !== publicId) navigate(`/${data.publicId}`, { replace: true })
      void api.track(publicId, 'workspace_view').catch(() => undefined)
    } catch (cause) {
      const status = (cause as Error & { status?: number }).status
      setGate(status === 404 ? 'missing' : entryState?.codeRequested ? 'code' : 'email')
    }
  }, [entryState?.codeRequested, navigate, publicId])

  useEffect(() => { void load() }, [load])

  async function requestCode(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      await api.requestCode(publicId, email)
      setGate('code')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'We could not send the code.')
    } finally {
      setBusy(false)
    }
  }

  async function verifyCode(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      const data = await api.verifyCode(publicId, email, code)
      setWorkspace(data)
      setGate('open')
      if (data.publicId !== publicId) navigate(`/${data.publicId}`, { replace: true })
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That code could not be verified.')
    } finally {
      setBusy(false)
    }
  }

  if (gate === 'loading') return <LoadingState />
  if (gate === 'missing') return <NotFoundPage />
  if (gate !== 'open' || !workspace) {
    return (
      <AccessGate
        stage={gate}
        email={email}
        code={code}
        error={error}
        busy={busy}
        onEmail={setEmail}
        onCode={setCode}
        onRequest={requestCode}
        onVerify={verifyCode}
        onBack={() => { setGate('email'); setCode(''); setError('') }}
      />
    )
  }

  return <WorkspaceExperience workspace={workspace} liveDocuments={!previewMode} onRefresh={previewMode ? undefined : async () => { setWorkspace(await api.loadWorkspace(publicId)) }} onGenerateQuote={previewMode || !workspace.pricingModel ? undefined : (inputs) => api.generateQuote(workspace.publicId, workspace.pricingModel!, inputs)} onRequestSandbox={previewMode ? undefined : (context) => api.requestSandbox(publicId, context).then(() => undefined)} onOrderFormActivity={previewMode ? undefined : reportOrderFormActivity} onTrack={previewMode ? undefined : (eventType, targetId) => {
    void api.track(publicId, eventType, targetId).catch(() => undefined)
    recordWorkspaceSession(eventType, targetId)
  }} />
}

function LoadingState() {
  return (
    <main className="state-page">
      <div className="loading-lockup"><SpinnerGap size={21} className="spin" /><span>Opening your workspace</span></div>
    </main>
  )
}

/** Copy for the sign-in gate. Customer workspaces use the defaults; the partner area passes its own. */
export interface AccessGateCopy {
  headline: string
  emailTitle: string
  emailIntro: string
  openLabel: string
  emailPlaceholder: string
}

const CUSTOMER_GATE_COPY: AccessGateCopy = {
  headline: 'Everything your team needs to evaluate, plan, and move forward',
  emailTitle: 'Open your workspace',
  emailIntro: 'Use your work email to access shared resources, next steps, and pricing.',
  openLabel: 'Open workspace',
  emailPlaceholder: 'you@university.edu',
}

/** The email, then six-digit code, sign-in step shared by customer workspaces and partner areas. */
export function AccessGate(props: {
  copy?: AccessGateCopy
  stage: 'email' | 'code' | Gate
  email: string
  code: string
  error: string
  busy: boolean
  onEmail: (value: string) => void
  onCode: (value: string) => void
  onRequest: (event: FormEvent) => void
  onVerify: (event: FormEvent) => void
  onBack: () => void
}) {
  const enteringCode = props.stage === 'code'
  const copy = props.copy ?? CUSTOMER_GATE_COPY
  return (
    <main className="access-page">
      <section className="access-brand">
        <img src="/prentus-logo-white.png" alt="Prentus" className="prentus-wordmark" />
        <div>
          <p>Your partnership, in one place.</p>
          <h1>{copy.headline}</h1>
        </div>
      </section>
      <section className="access-form-wrap">
        <form className="access-form" onSubmit={enteringCode ? props.onVerify : props.onRequest}>
          <span className="lock-icon"><LockKey size={22} /></span>
          <h2>{enteringCode ? 'Check your inbox' : copy.emailTitle}</h2>
          <p>{enteringCode ? `Enter the six-digit code sent to ${props.email}.` : copy.emailIntro}</p>
          <label>
            <span>{enteringCode ? 'Verification code' : 'Work email'}</span>
            <input
              type={enteringCode ? 'text' : 'email'}
              inputMode={enteringCode ? 'numeric' : undefined}
              autoComplete={enteringCode ? 'one-time-code' : 'email'}
              value={enteringCode ? props.code : props.email}
              placeholder={enteringCode ? '000000' : copy.emailPlaceholder}
              onChange={(event) => enteringCode ? props.onCode(event.target.value.replace(/\D/g, '').slice(0, 6)) : props.onEmail(event.target.value)}
            />
          </label>
          {props.error && <div className="form-error" role="alert">{props.error}</div>}
          <button className="button button-primary full" disabled={props.busy || (enteringCode ? props.code.length !== 6 : !props.email)}>
            {props.busy && <SpinnerGap size={17} className="spin" />}
            {enteringCode ? copy.openLabel : 'Send verification code'}
          </button>
          {enteringCode && <button type="button" className="back-link" onClick={props.onBack}><ArrowLeft size={15} />Use a different email</button>}
        </form>
      </section>
    </main>
  )
}
